import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { PatientAppointment } from 'src/app/entities/appointment.entity/patient-appointment.entity/patient-appointment';
import { User } from 'src/app/entities/user.entity';
import { Doctor } from 'src/app/entities/user.entity/doctor.entity';
import { AuthService } from 'src/app/services/auth.service/auth.service';
import { AppointmentPatientLoginComponent } from '../appointment-patient-login.component/appointment-patient-login.component';
import { PatientRequestedAppointment } from 'src/app/entities/appointment.entity';
import { PublicApisService } from 'src/app/services/public-apis.service/public-apis.service';
import { Country, Language, LocalizationService } from 'src/app/services/localization.service/localization.service';
import { RegexConstants } from 'src/app/shared/constants/regex.constants';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';

@Component({
  selector: 'app-appointment-booking-verification',
  templateUrl: './appointment-booking-verification.component.html',
  styleUrls: ['./appointment-booking-verification.component.css']
})
export class AppointmentBookingVerificationComponent implements OnInit,OnChanges {

  @Input() doctor!: Doctor;
  @Input() patientAppointment!: PatientAppointment;
  @Input() isSubmited: boolean = false;
  @Input() dir: string = "fr";
  @Output() complete: EventEmitter<PatientAppointment> = new EventEmitter();

  @ViewChild(AppointmentPatientLoginComponent, {
    read: AppointmentPatientLoginComponent,
    static: false
  })
  appointmentPatientLoginComponent!: AppointmentPatientLoginComponent;

  appointmentRequestForm: FormGroup;
  countries: Array<Country> = [];
  dialCode: string = "+216";
language:Language = 'fr';
  defaultCountry: Country = {
    name: this.language === 'ar' ? "تونس" : "Tunisie",
    dialCode: "+216",
    isoCode: "TN",
    flagUrl: "https://cdn.kcak11.com/CountryFlags/countries/tn.svg",
    timeOffset: 1
  };

  constructor(
    private authService: AuthService,
    private publicApiService: PublicApisService,
    private localizationService: LocalizationService,
    private formBuilder: FormBuilder
  ) {
    this.appointmentRequestForm = this.formBuilder.group({
      firstName: ["", [Validators.required, CustomValidators.alphabetOnlyValidator]],
      lastName: ["", [Validators.required, CustomValidators.alphabetOnlyValidator]],
      mobilePhone: [null],
      birthDate: [new Date(1970, 1, 1), [Validators.required]],
      country: [this.defaultCountry, [Validators.required]],
      email: ["", [Validators.email]]
    });

    this.localizationService.getLanguage().subscribe(language => {
      this.language = language;
    })
    this.countries = this.localizationService.getAllCountries(this.language);
    
  }

  ngOnInit(): void {
    this.patientAppointment.requested = true;
    this.onCountryChanged(this.defaultCountry);
  }
  ngOnChanges(simpl: SimpleChanges): void {
    this.language = simpl['dir']?.currentValue || this.language;
    this.countries = this.localizationService.getAllCountries(this.language);
    this.defaultCountry = {
      name: this.language === 'ar' ? "تونس" : "Tunisie",
      dialCode: "+216",
    isoCode: "TN",
    flagUrl: "https://cdn.kcak11.com/CountryFlags/countries/tn.svg",
    timeOffset: 1
    }
    this.appointmentRequestForm.controls.country.setValue(this.countries.find(c => c.isoCode === this.defaultCountry.isoCode) || this.defaultCountry);

  }
  get valid(): boolean {
    return this.patientAppointment.requested
      ? this.appointmentRequestForm.valid
      : this.appointmentPatientLoginComponent?.valid;
  }

  get patient(): User | null {
    return this.authService.getCurrentUser();
  }

  get f() {
    return this.appointmentRequestForm.controls;
  }

  /**
   * TN => téléphone obligatoire
   * Hors TN => email obligatoire
   */
  onCountryChanged(country?: Country): void {
    const selectedCountry = country || this.appointmentRequestForm.get('country')?.value || this.defaultCountry;

    this.dialCode = selectedCountry?.dialCode || '';

    const isTunisia = selectedCountry?.isoCode === 'TN'
      || selectedCountry?.name?.toLowerCase() === 'tunisie' 
      || selectedCountry?.name?.toLowerCase() === 'tunisia';

    const phoneControl = this.appointmentRequestForm.get('mobilePhone');
    const emailControl = this.appointmentRequestForm.get('email');

    if (!phoneControl || !emailControl) return;

    if (isTunisia) {
      // Téléphone obligatoire pour Tunisie
      phoneControl.setValidators([
        Validators.required,
        Validators.pattern(RegexConstants.PHONE_TN)
      ]);

      // Email optionnel mais s'il existe il doit être valide
      emailControl.setValidators([
        Validators.email
      ]);
    } else {
      // Téléphone optionnel hors Tunisie
      phoneControl.setValidators([
        Validators.pattern(/^[0-9]{6,15}$/)
      ]);

      // Email obligatoire hors Tunisie
      emailControl.setValidators([
        Validators.required,
        Validators.email
      ]);
    }

    phoneControl.updateValueAndValidity();
    emailControl.updateValueAndValidity();
  }

  submit(): Promise<PatientAppointment> {
    this.isSubmited = true;

    if (this.patientAppointment.requested) {
      return new Promise((resolve, reject) => {
        // sécurité si pays a changé sans déclencher le dropdown
        this.onCountryChanged(this.appointmentRequestForm.controls.country.value);

        if (!this.appointmentRequestForm.valid) {
          reject();
          return;
        }

        const firstName = this.appointmentRequestForm.controls.firstName.value?.trim();
        const lastName = this.appointmentRequestForm.controls.lastName.value?.trim();
        const mobilePhone = this.appointmentRequestForm.controls.mobilePhone.value?.trim();
        const birthDate = this.appointmentRequestForm.controls.birthDate.value;
        const email = this.appointmentRequestForm.controls.email.value?.trim();
        const country = this.appointmentRequestForm.controls.country.value;

        this.patientAppointment.patientFirstName = firstName;
        this.patientAppointment.patientLastName = lastName;
        this.patientAppointment.patientBirthDate = birthDate;
        this.patientAppointment.patientCountry = country?.name || '';

        // téléphone formaté uniquement s'il existe
        this.patientAppointment.patientMobilePhone = mobilePhone
          ? `${this.dialCode} ${mobilePhone}`
          : null as any;

        // email null si vide
        this.patientAppointment.patientEmail = email ? email : null;
        this.patientAppointment.requested = true;

        this.complete.emit(this.patientAppointment);

        this.publicApiService
          .requestAppointment(this.doctor?.id!, new PatientRequestedAppointment(this.patientAppointment))
          .subscribe({
            next: (response) => {
              response.doctor = this.doctor;
              response.requested = true;
              resolve(PatientAppointment.fromResponse(response));
            },
            error: (error) => reject(error)
          });
      });
    }

    return this.appointmentPatientLoginComponent.onSubmit();
  }
}